import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useRecognitionRuns } from "@/hooks/useSubscriptionSchedules";
import type { RecognitionRunStatus } from "@/interfaces/subscriptionSchedules";
import { formatDate, formatDateTime } from "@/utils/date-format";
import RecognitionRunBar, { releasedPeriod } from "./RecognitionRunBar";
import { BackLink, cell, num, periodLabel, tableBox, th } from "./shared";

const PAGE_SIZE = 20;

const STATUS: Record<
  RecognitionRunStatus,
  { label: string; color: "success" | "warning" | "error" | "default" }
> = {
  succeeded: { label: "Succeeded", color: "success" },
  failed: { label: "Failed", color: "error" },
  missed: { label: "Missed", color: "warning" },
  running: { label: "Running", color: "default" },
};

/**
 * Every run of the monthly recognition, newest first: what it was for, how it ended, what it posted,
 * what failed or was still due, and who started it. Rerun from the bar at the top.
 */
export default function RecognitionRunsView({ onBack }: { onBack: () => void }) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useRecognitionRuns(page, PAGE_SIZE);

  return (
    <Box>
      <BackLink label="Deferred Subscriptions" onClick={onBack} />
      <Typography component="h1" sx={{ fontSize: 22, fontWeight: 700, mt: 1 }}>
        Recognition runs
      </Typography>
      <Typography sx={{ fontSize: 13, color: "var(--color-graphite)", mb: 2.5 }}>
        The monthly run that releases subscription revenue for the month just ended. It runs on the
        1st; a failed or missed run is rerun here, and a month can't be closed until its releases
        post.
      </Typography>

      <RecognitionRunBar />

      <Box sx={tableBox}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={th}>Run</TableCell>
              <TableCell sx={th}>Releases</TableCell>
              <TableCell sx={th}>Status</TableCell>
              <TableCell sx={th}>Started</TableCell>
              <TableCell sx={th}>Finished</TableCell>
              <TableCell sx={{ ...th, textAlign: "right" }}>Posted</TableCell>
              <TableCell sx={{ ...th, textAlign: "right" }}>Failed</TableCell>
              <TableCell sx={{ ...th, textAlign: "right" }}>Still due after</TableCell>
              <TableCell sx={th}>Started by</TableCell>
              <TableCell sx={th}>Detail</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={10} sx={{ ...cell, color: "var(--color-graphite)" }}>
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {data && data.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} sx={{ ...cell, color: "var(--color-graphite)" }}>
                  No runs yet. The first runs on the 1st of next month.
                </TableCell>
              </TableRow>
            )}
            {data?.data.map((r) => {
              const s = STATUS[r.status];
              const missed = r.status === "missed";
              return (
                <TableRow key={r.id} hover>
                  <TableCell sx={cell}>{formatDate(r.scheduledFor)}</TableCell>
                  <TableCell sx={cell}>{periodLabel(releasedPeriod(r.scheduledFor))}</TableCell>
                  <TableCell sx={cell}>
                    <Chip size="small" variant="outlined" color={s.color} label={s.label} />
                  </TableCell>
                  <TableCell sx={cell}>{missed ? "—" : formatDateTime(r.startedAt)}</TableCell>
                  <TableCell sx={cell}>
                    {r.finishedAt && !missed ? formatDateTime(r.finishedAt) : "—"}
                  </TableCell>
                  <TableCell sx={num}>{missed ? "—" : r.postedCount}</TableCell>
                  <TableCell
                    sx={{
                      ...num,
                      color: r.failedCount ? "var(--color-error, #b3261e)" : undefined,
                    }}
                  >
                    {missed ? "—" : r.failedCount}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...num,
                      color: r.stillDueCount ? "var(--color-error, #b3261e)" : undefined,
                    }}
                  >
                    {missed ? "—" : r.stillDueCount}
                  </TableCell>
                  <TableCell sx={cell}>{missed ? "—" : (r.triggeredBy ?? "Scheduled")}</TableCell>
                  <TableCell sx={{ fontSize: 12.5, color: "var(--color-graphite)", maxWidth: 420 }}>
                    {r.error ?? ""}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      {data && data.totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1,
            mt: 1.5,
          }}
        >
          <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
            Page {page} of {data.totalPages}
          </Typography>
          <Button
            size="small"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
            sx={{ textTransform: "none" }}
          >
            Newer
          </Button>
          <Button
            size="small"
            disabled={page >= data.totalPages}
            onClick={() => setPage(page + 1)}
            sx={{ textTransform: "none" }}
          >
            Older
          </Button>
        </Box>
      )}
    </Box>
  );
}
